/**
 * authenticatedFetch — drop-in replacement for `fetch()` that intercepts 401
 * "Not authenticated" responses, silently refreshes the SSO session, and
 * retries the original request exactly once with the new token.
 *
 * If the silent refresh fails (SSO truly expired), the auth store is cleared
 * which triggers the login page redirect.
 *
 * Usage:  import { authenticatedFetch } from '../services/authenticatedFetch';
 *         const response = await authenticatedFetch('/api/chat', { method: 'POST', ... });
 */

import { useAuthStore } from '../stores/authStore';
import { msalInstance, ensureMsalInitialized, loginScopes } from './msalInstance';

// Singleton refresh promise so concurrent 401s don't stampede the endpoint.
let activeRefreshPromise: Promise<string | null> | null = null;

/**
 * Hash a token string using SHA-256 (Web Crypto API).
 * Used to send the previous token hash so the backend can clean up old sessions.
 */
async function hashToken(token: string): Promise<string> {
  const data = new TextEncoder().encode(token);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Acquire a fresh MSAL token and register it with the backend's session store.
 */
async function silentRefresh(): Promise<string | null> {
  await ensureMsalInitialized();

  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  if (!account) {
    return null;
  }

  msalInstance.setActiveAccount(account);

  const result = await msalInstance.acquireTokenSilent({
    account,
    scopes: loginScopes,
    forceRefresh: true,
  });

  const accessToken = result.accessToken;
  if (!accessToken) {
    return null;
  }

  // Build request body — include previous token hash so backend can clean up
  const previousToken = useAuthStore.getState().token;
  const body: Record<string, string> = { accessToken };
  if (previousToken) {
    body.previousTokenHash = await hashToken(previousToken);
  }

  // Register the refreshed token with the backend
  const response = await fetch('/api/auth/token-refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (response.ok) {
    const payload = await response.json().catch(() => null);
    // Use the user object returned by the backend (fresh roles/profile)
    const returnedUser = payload?.user ?? payload?.data?.user;
    if (returnedUser) {
      useAuthStore.getState().loginWithToken(accessToken, returnedUser);
    } else {
      // Fallback to current user if backend didn't return one
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        useAuthStore.getState().loginWithToken(accessToken, currentUser);
      }
    }
    return accessToken;
  }

  // Fallback: try the full login exchange
  const loginResponse = await fetch('/api/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ accessToken }),
  });

  const loginPayload = await loginResponse.json().catch(() => null);
  if (!loginResponse.ok || !loginPayload?.success) {
    return null;
  }

  // Use the user from the login response instead of stale currentUser
  const loginUser = loginPayload.user ?? loginPayload.data?.user;
  if (loginUser) {
    useAuthStore.getState().loginWithToken(accessToken, loginUser);
  }

  return accessToken;
}

/**
 * Coalesce concurrent refreshes into a single promise.
 */
function performSilentRefresh(): Promise<string | null> {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = silentRefresh()
    .catch(() => null)
    .finally(() => {
      activeRefreshPromise = null;
    });

  return activeRefreshPromise;
}

/**
 * Replace the Authorization header in the original request init with the
 * refreshed token.
 */
function replaceAuthHeader(init: RequestInit | undefined, newToken: string): RequestInit {
  const updated = { ...(init ?? {}) };
  const headers = new Headers(updated.headers as HeadersInit ?? {});
  headers.set('Authorization', `Bearer ${newToken}`);
  updated.headers = headers;
  return updated;
}

/**
 * Authenticated fetch wrapper.
 *
 * - On 401: silently refresh the token and retry the request once.
 * - On refresh failure: clear session to trigger login redirect.
 */
export async function authenticatedFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status !== 401) {
    return response;
  }

  // Attempt silent refresh
  const newToken = await performSilentRefresh();

  if (!newToken) {
    // SSO is truly expired — clear session to trigger login page
    useAuthStore.getState().clearSession();
    return response;
  }

  // Retry the original request with the new token
  const retryInit = replaceAuthHeader(init, newToken);
  return fetch(input, retryInit);
}

export { performSilentRefresh };
export default authenticatedFetch;
